'use client';

import { useState } from 'react';
import { PartyPopper, ChevronRight } from 'lucide-react';

interface TourCompleteModalProps {
  onFinish: () => void;
  isSubmitting: boolean;
}

/**
 * TourCompleteModal — centered modal shown at the end of the AdminTour.
 * Displays a two-screen congratulatory message with navigation tips
 * and next steps for phone number registration.
 */
export function TourCompleteModal({ onFinish, isSubmitting }: TourCompleteModalProps) {
  const [currentScreen, setCurrentScreen] = useState(0);
  const isLastScreen = currentScreen === 1;

  const handleNext = () => {
    if (isLastScreen) {
      onFinish();
    } else {
      setCurrentScreen(1);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Tour complete"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Modal card */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-8">
        {/* Icon */}
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-accent-blue/[0.08]">
          <PartyPopper className="h-8 w-8 text-accent-blue" />
        </div>

        {/* Title */}
        <h2 className="text-2xl font-bold mb-4 text-center text-gray-900">You&rsquo;re All Set!</h2>

        {/* Content - Screen 1 */}
        {currentScreen === 0 && (
          <div className="text-left space-y-4 mb-8">
            <p className="text-base leading-relaxed text-gray-500">
              You now have a general understanding of how to navigate the app. Visit the Help page
              to find detailed guides on using the tools to get the most out of your platform.
            </p>
          </div>
        )}

        {/* Content - Screen 2 */}
        {currentScreen === 1 && (
          <div className="text-left space-y-4 mb-8">
            <p className="text-base leading-relaxed text-gray-500">
              To unlock messaging, register a phone number and file a 10DLC registration. In the
              meantime, explore the platform, invite your team, and configure templates, tags, and
              other settings specific to your agency.
            </p>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-center">
          <button
            onClick={handleNext}
            disabled={isSubmitting}
            aria-label={isLastScreen ? 'Finish tour' : 'Next screen'}
            className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full text-base font-semibold text-white bg-accent-blue hover:brightness-110 transition-all shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Finishing...
              </>
            ) : isLastScreen ? (
              'Finish Tour'
            ) : (
              <>
                Next
                <ChevronRight className="h-4 w-4" />
              </>
            )}
          </button>
        </div>

        {/* Step indicators */}
        <div className="flex justify-center gap-1.5 mt-5">
          {[0, 1].map((i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all duration-200 ${
                i === currentScreen ? 'w-6 bg-accent-blue' : 'w-1.5 bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
